import type {
  TimeSeriesChartCommand,
  TimeSeriesChartState,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { SINGLE_SCALE_AXIS_ID } from 'features/timeSeries/components/timeSeriesScatterplot/yAxisOverlayLabels'
import { computedPenId } from 'features/timeSeries/computed/computedTrendStore'
import type { EmbeddedTrendView } from 'features/timeSeries/computed/EmbeddedWorkbenchContext'

import {
  plainPenIds,
  SHARED_AXIS_KEY,
  type TrendPreview,
  type TrendPreviewYRanges,
  yRangesFromWire,
} from './trendPreview'

function union(...lists: Array<readonly string[] | undefined>): string[] {
  return [...new Set(lists.flatMap((list) => list ?? []))]
}

function without(list: readonly string[] | undefined, removed: Set<string>) {
  return (list ?? []).filter((id) => !removed.has(id))
}

/**
 * Fold one agent chart command into the preview. Pens named in the command
 * are entry ids; computed series travel alongside and are plotted by their
 * own ids. `focus` and `add_pens` accumulate, `remove_pens` subtracts,
 * `replace_pens` shows exactly the command's contents; window, bins, axis
 * ranges and label apply whenever the command carries them. Bands and
 * aggregation are not rendered by the embedded chart and are dropped here.
 */
export function applyChartCommand(
  base: TrendPreview | null,
  command: TimeSeriesChartCommand,
  fallbackLabel: string
): TrendPreview {
  const incomingComputed = command.computed_series ?? []
  const computedIds = new Set(incomingComputed.map((series) => series.entry_id))
  const plain = (command.pens ?? []).filter((id) => !computedIds.has(id))
  const removed = new Set(command.pens ?? [])
  const basePlain = base ? plainPenIds(base) : []
  const baseComputed = base?.computedSeries ?? []

  let pens: string[]
  let computed: TrendPreview['computedSeries']
  let hidden: string[]
  if (command.op === 'replace_pens') {
    pens = plain
    computed = incomingComputed.length ? incomingComputed : undefined
    hidden = command.hide ?? []
  } else if (command.op === 'remove_pens') {
    pens = without(basePlain, removed)
    const kept = baseComputed.filter((series) => !removed.has(series.entry_id))
    computed = kept.length ? kept : undefined
    hidden = without(base?.hiddenPens, removed)
  } else {
    pens = union(basePlain, plain)
    const byId = new Map(
      [...baseComputed, ...incomingComputed].map((series) => [
        series.entry_id,
        series,
      ])
    )
    computed = byId.size ? [...byId.values()] : undefined
    hidden = union(without(base?.hiddenPens, new Set(plain)), command.hide)
  }

  const shown = new Set([
    SHARED_AXIS_KEY,
    ...pens,
    ...(computed ?? []).map((series) => series.entry_id),
  ])
  const merged = { ...base?.yRanges, ...yRangesFromWire(command.y_ranges) }
  const kept = Object.entries(merged).filter(([id]) => shown.has(id))
  const yRanges = kept.length ? Object.fromEntries(kept) : undefined
  return {
    entryIds: pens,
    contextEntryIds: pens,
    computedSeries: computed,
    label: command.label ?? base?.label ?? fallbackLabel,
    startDatetime: command.start_datetime ?? base?.startDatetime,
    endDatetime: command.end_datetime ?? base?.endDatetime,
    sessionKey: base?.sessionKey,
    marker: base?.marker,
    hiddenPens: hidden.length ? hidden : undefined,
    yRanges,
    interval: command.interval ?? base?.interval,
    intervalUnit: command.interval_unit ?? base?.intervalUnit,
  }
}

/**
 * The preview after the user zoomed, re-penned or re-scaled the embedded
 * chart. Computed series keep their own ids; an empty pen list is ignored so a
 * chart that is still loading cannot wipe the preview's pens. Axis ranges come
 * back in the agent's vocabulary: the shared axis as `all`, computed pens by
 * their series id, and ranges for pens the chart does not plot are dropped.
 */
export function applyWorkbenchView(
  base: TrendPreview,
  view: EmbeddedTrendView
): TrendPreview {
  const computedIds = new Set(
    (base.computedSeries ?? []).map((series) => series.entry_id)
  )
  const pens = view.sensorIds.filter((id) => !computedIds.has(id))
  return {
    ...base,
    ...(pens.length ? { entryIds: pens, contextEntryIds: pens } : {}),
    startDatetime: view.start,
    endDatetime: view.end,
    interval: view.interval ?? base.interval,
    intervalUnit: view.intervalUnit ?? base.intervalUnit,
    hiddenPens: view.hiddenSensorIds.length ? view.hiddenSensorIds : undefined,
    yRanges: reportedYRanges(view, [...computedIds]),
  }
}

function reportedYRanges(
  view: EmbeddedTrendView,
  computedIds: string[]
): TrendPreviewYRanges | undefined {
  const ranges = yRangesFromWire(view.customAxisRanges)
  if (!ranges) return undefined
  const byPenId = new Map(computedIds.map((id) => [computedPenId(id), id]))
  const plain = new Set(view.sensorIds)
  const out: TrendPreviewYRanges = {}
  for (const [key, range] of Object.entries(ranges)) {
    if (key === SINGLE_SCALE_AXIS_ID) {
      if (view.singleScale) out[SHARED_AXIS_KEY] = range
    } else if (byPenId.has(key)) {
      out[byPenId.get(key) ?? key] = range
    } else if (plain.has(key)) {
      out[key] = range
    }
  }
  return Object.keys(out).length ? out : undefined
}

function viewFields(preview: TrendPreview) {
  return {
    pens: plainPenIds(preview),
    start: preview.startDatetime ?? null,
    end: preview.endDatetime ?? null,
    interval: preview.interval ?? null,
    intervalUnit: preview.intervalUnit ?? null,
    hidden: preview.hiddenPens ?? [],
    yRanges: preview.yRanges ?? null,
  }
}

/** True when both previews would be reported to the agent as the same chart. */
export function sameChartView(a: TrendPreview, b: TrendPreview): boolean {
  return JSON.stringify(viewFields(a)) === JSON.stringify(viewFields(b))
}

/** The chart as the client will report it: in the request and in acknowledgements. */
export function chartStateFromPreview(
  preview: TrendPreview | null,
  revision: number
): TimeSeriesChartState | undefined {
  if (!preview) return undefined
  return {
    chart_id: preview.sessionKey ?? null,
    revision,
    pens: plainPenIds(preview),
    computed_pens: (preview.computedSeries ?? []).map(
      (series) => series.entry_id
    ),
    hidden_pens: preview.hiddenPens ?? [],
    start_datetime: preview.startDatetime ?? null,
    end_datetime: preview.endDatetime ?? null,
    interval: preview.interval ?? null,
    interval_unit: preview.intervalUnit ?? null,
    aggregation_op: null,
    y_ranges: preview.yRanges ?? null,
    band: null,
  }
}
